import React, { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { CrewDataTableFrame, CrewDataTablePrompt, ReportTextCell } from './reportShared';
import { Select, VesselSelect } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import CrewReportSearchHeader, { CrewReportFilterField } from './CrewReportSearchHeader';
import type { CrewChangesPerVesselResponse, CrewChangesPerVesselRow } from './types';

function currentYear(): number {
  return new Date().getFullYear();
}

function yearOptions(span = 12): Array<{ value: string; label: string }> {
  const y = currentYear();
  return Array.from({ length: span }, (_, i) => {
    const year = y - i;
    return { value: String(year), label: String(year) };
  });
}

const NumCell: React.FC<{ value: number | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return (
    <span className={cn('text-sm tabular-nums whitespace-nowrap', colors.text)}>
      {value == null ? '-' : value.toLocaleString()}
    </span>
  );
};

const CrewChangesPerVesselPage: React.FC = () => {
  const { colors } = useTheme();
  const [vesselId, setVesselId] = useState('');
  const [year, setYear] = useState(String(currentYear()));
  const [applied, setApplied] = useState<{ vesselId: string; year: string } | null>(null);

  const years = useMemo(() => yearOptions(), []);

  const listUrl = applied
    ? `/crew/changes-per-vessel?year=${encodeURIComponent(applied.year)}${
        applied.vesselId ? `&vesselId=${encodeURIComponent(applied.vesselId)}` : ''
      }`
    : null;

  const { data, error, isLoading, mutate } = useSWR<CrewChangesPerVesselResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const handleSearch = useCallback(() => {
    if (!year) return;
    setApplied({ vesselId, year });
  }, [vesselId, year]);

  const rows = data?.rows ?? [];

  const columns: Column<CrewChangesPerVesselRow>[] = useMemo(
    () => [
      {
        key: 'vessel',
        header: 'Vessel',
        sortable: true,
        render: (_v, row) => (
          <span className={cn('text-sm whitespace-nowrap', colors.text)}>
            {row.vessel}
            {row.vesselAbrv ? ` (${row.vesselAbrv})` : ''}
          </span>
        ),
      },
      {
        key: 'q1',
        header: '1st Quarter',
        sortable: true,
        footerType: 'sum',
        footerRender: (sum) => (typeof sum === 'number' ? sum.toLocaleString() : '-'),
        render: (v) => <NumCell value={v} />,
      },
      {
        key: 'q2',
        header: '2nd Quarter',
        sortable: true,
        footerType: 'sum',
        footerRender: (sum) => (typeof sum === 'number' ? sum.toLocaleString() : '-'),
        render: (v) => <NumCell value={v} />,
      },
      {
        key: 'q3',
        header: '3rd Quarter',
        sortable: true,
        footerType: 'sum',
        footerRender: (sum) => (typeof sum === 'number' ? sum.toLocaleString() : '-'),
        render: (v) => <NumCell value={v} />,
      },
      {
        key: 'q4',
        header: '4th Quarter',
        sortable: true,
        footerType: 'sum',
        footerRender: (sum) => (typeof sum === 'number' ? sum.toLocaleString() : '-'),
        render: (v) => <NumCell value={v} />,
      },
      {
        key: 'total',
        header: 'Total',
        sortable: true,
        footerType: 'sum',
        footerRender: (sum) => (typeof sum === 'number' ? sum.toLocaleString() : '-'),
        render: (v) => (
          <span className={cn('text-sm font-semibold tabular-nums whitespace-nowrap', colors.text)}>
            {v == null ? '-' : Number(v).toLocaleString()}
          </span>
        ),
      },
    ],
    [colors.text],
  );

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <CrewReportSearchHeader
        hint={{
          title: 'Total Crew Changes per Vessel',
          body: 'Counts sign-ons and sign-offs by quarter for the selected year. Optionally filter to one vessel, or leave vessel blank to compare all vessels.',
        }}
        onSearch={handleSearch}
        searchDisabled={!year}
        loading={isLoading}
        onRefresh={applied ? () => mutate() : undefined}
        context={
          applied
            ? `Crew changes in ${applied.year}${
                applied.vesselId
                  ? ` · ${data?.rows?.[0]?.vessel ?? 'Selected vessel'}`
                  : ' · All vessels'
              } · Counts sign-ons and sign-offs per quarter`
            : undefined
        }
      >
        <CrewReportFilterField label="Vessel" htmlFor="crew-changes-vessel" width="lg">
          <VesselSelect
            name="vesselId"
            value={vesselId}
            onChange={(e) => setVesselId(String(e.target.value))}
            placeholder="All vessels"
            includeIds={vesselId ? [vesselId] : undefined}
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Year" htmlFor="crew-changes-year" width="sm">
          <Select
            name="year"
            value={year}
            onChange={(e) => setYear(String(e.target.value))}
            options={years}
            placeholder="Year"
            showClearButton={false}
          />
        </CrewReportFilterField>
      </CrewReportSearchHeader>

      <CrewDataTableFrame>
        {!applied ? (
          <CrewDataTablePrompt>Select a year (and optionally a vessel), then search.
          </CrewDataTablePrompt>
        ) : error ? (
          <CrewDataTablePrompt><span className="text-red-600">
            Could not load crew changes report.
          </span></CrewDataTablePrompt>
        ) : (
          <DataTable<CrewChangesPerVesselRow>
            data={rows}
            columns={columns}
            keyExtractor={(row) => row.vesselId}
            loading={isLoading}
            emptyMessage="No data to display"
            showFooter
            footerData={rows}
          />
        )}
      </CrewDataTableFrame>

      {applied && data?.summary && (
        <div
          className={cn(
            'flex flex-wrap items-center gap-6 rounded-xl border px-4 py-3 text-sm shadow-sm',
            colors.surface,
            colors.border,
          )}
        >
          <div>
            <span className={colors.textSecondary}>Vessels</span>
            <span className={cn('ml-2 font-semibold', colors.text)}>{rows.length}</span>
          </div>
          <div>
            <span className={colors.textSecondary}>Total changes</span>
            <span className={cn('ml-2 font-semibold', colors.text)}>
              {data.summary.total.toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrewChangesPerVesselPage;
